import React from "react";
import {
  Button,
  Dropdown,
  Pagination,
  PaginationProps,
  Table,
  TableColumnsType,
} from "antd";
import { TableRowSelection } from "antd/es/table/interface";
import { PlusIcon, SelectArrow } from "../CustomIcons";
import { useNavigate } from "react-router-dom";
import { createStyles } from "antd-style";
import { MoreOutlined } from "@ant-design/icons";

interface TableProps<T extends object> {
  columns: TableColumnsType<T>;
  data: {
    count: number;
    data: T[];
  };
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  filters?: JSX.Element;
  createButtonText?: string;
  createButtonLink?: string;
  selectedRowKeys: React.Key[];
  currentPage: number;
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  onClickRow?: (record: T) => {
    onClick: () => void;
  };
  createButtonIcon?: React.ReactNode;
  items?: {
    key: string;
    label: JSX.Element;
  }[];
  createButtonItem?: {
    key: string;
    label: JSX.Element;
  }[];
}

const TableUi = <T extends object>(props: TableProps<T>) => {
  const {
    data,
    columns,
    pageSize,
    filters,
    createButtonText,
    createButtonLink,
    selectedRowKeys,
    currentPage,
    setPageSize,
    setSelectedRowKeys,
    setCurrentPage,
    onClickRow,
    createButtonItem,
    createButtonIcon,
    items,
  } = props;

  const useStyle = createStyles(({ css }) => {
    return {
      customTable: css`
        .ant-table {
          .ant-table-container {
            .ant-table-body,
            .ant-table-content {
              scrollbar-width: thin;
              scrollbar-color: transparent transparent;
              scrollbar-gutter: stable;
              scrollbar-border-radius: 30px;
              transition: scrollbar-color 0.5s;

              &:hover {
                scrollbar-color: #dee5e6 transparent;
              }
            }
          }
        }
      `,
    };
  });
  const { styles } = useStyle();

  const navigate = useNavigate();

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<T> = {
    selectedRowKeys,
    onChange: onSelectChange,
    type: "checkbox",
  };

  const itemRender: PaginationProps["itemRender"] = (_, type) => {
    if (type === "prev") {
      return (
        <div className="table_prev_btn">
          <SelectArrow />
        </div>
      );
    }
    if (type === "next") {
      return (
        <div className="table_next_btn">
          <SelectArrow />
        </div>
      );
    }
    return undefined; // Or return originalElement if you want to render default
  };

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  // const paginatedData = useMemo(() => {
  //   const startIndex = (currentPage - 1) * pageSize;
  //   const endIndex = startIndex + pageSize;
  //   return data?.data?.slice(startIndex, endIndex);
  // }, [data, currentPage, pageSize]);

  return (
    <div className="table_with_filter">
      <div className="top_filter">
        {filters && filters}
        <div className="right_filter">
          {createButtonText &&
          createButtonItem &&
          createButtonItem.length > 0 ? (
            <Dropdown
              menu={{ items: createButtonItem }}
              trigger={["click"]}
              placement="bottomRight"
              className="actionButton"
            >
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onKeyDown={(e) => e.stopPropagation()}
                type="primary"
                icon={createButtonIcon}
              >
                {createButtonText}
              </Button>
            </Dropdown>
          ) : (
            createButtonText && (
              <Button
                icon={<PlusIcon />}
                type="primary"
                onClick={() =>
                  createButtonLink ? navigate(createButtonLink) : null
                }
              >
                {createButtonText}
              </Button>
            )
          )}

          {items && items.length > 0 && (
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
              className="actionButton btn_radius"
            >
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onKeyDown={(e) => e.stopPropagation()}
                style={{
                  padding: "10px",
                  height: "auto",
                  border: "1px solid #DEE5E6",
                  backgroundColor: "transparent",
                }}
                className="only_icon"
                icon={<MoreOutlined />}
              />
            </Dropdown>
          )}

          <Pagination
            itemRender={itemRender}
            total={data?.count}
            showTotal={(total, range) =>
              `${range?.[0]}-${range?.[1] ?? range?.[0]} of ${total ?? "0"}`
            }
            onChange={handlePageChange}
            current={currentPage}
            pageSize={pageSize}
          />
        </div>
      </div>

      {data?.count > 0 && (
        <Table<T>
          className={
            onClickRow
              ? `cursor_table ${styles.customTable}`
              : styles.customTable
          }
          rowSelection={rowSelection}
          columns={columns}
          dataSource={data?.data}
          scroll={{ x: "max-content" }}
          pagination={false} // Disable default table pagination, we have our own
          // rowKey={(record: T) => (record as { id: React.Key }).id} // Assuming each record has an id
          rowKey="id"
          onRow={onClickRow ? onClickRow : undefined}
        />
      )}
    </div>
  );
};

export default TableUi;
